import { create } from 'zustand';

interface SidebarState {
  isOpen: boolean;
  isMobileMenuOpen: boolean;
  isModalOpen: boolean;
  userName: string | null;
  userLastName: string | null;
  userId: string | null;
  userEmail: string | null;
  userImage: string;
  isUserDataLoaded: boolean;
  isUserDataLoading: boolean;
  setIsOpen: (open: boolean) => void;
  setIsMobileMenuOpen: (open: boolean) => void;
  setIsModalOpen: (open: boolean) => void;
  setUserName: (name: string | null) => void;
  setUserLastName: (lastName: string | null) => void;
  setUserId: (id: string | null) => void;
  setUserEmail: (email: string | null) => void;
  setUserImage: (image: string) => void;
  fetchUserData: () => Promise<void>;
  handleLogout: () => Promise<void>;
}

const getInitialSidebarState = (): boolean => {
  if (typeof window === "undefined") return true;
  try {
    const persisted = localStorage.getItem("sidebarExpanded");
    if (persisted === "true") return true;
    if (persisted === "false") return false;
    return true;
  } catch {
    return true; 
  }
};

export const useSidebarStore = create<SidebarState>((set, get) => ({
  isOpen: getInitialSidebarState(),
  isMobileMenuOpen: false,
  isModalOpen: false,
  userName: null,
  userLastName: null,
  userId: null,
  userEmail: null,
  userImage: '',
  isUserDataLoaded: false,
  isUserDataLoading: false,

  setIsOpen: (open) => {
    set({ isOpen: open });
    // Persist to localStorage
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem("sidebarExpanded", open ? "true" : "false");
      }
    } catch {}
  },
  setIsMobileMenuOpen: (open) => set({ isMobileMenuOpen: open }),
  setIsModalOpen: (open) => set({ isModalOpen: open }),
  setUserName: (name) => set({ userName: name }),
  setUserLastName: (lastName) => set({ userLastName: lastName }),
  setUserId: (id) => set({ userId: id }),
  setUserEmail: (email) => set({ userEmail: email }),
  setUserImage: (image) => set({ userImage: image }),

  fetchUserData: async () => {
    const state = get();
    if (state.isUserDataLoaded || state.isUserDataLoading) {
      return;
    }

    set({ isUserDataLoading: true });

    const maxRetries = 3;
    let retryCount = 0;
    let lastError: Error | null = null;

    while (retryCount <= maxRetries) {
      try {
        const response = await fetch("/api/auth/me", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          if (response.status === 401) {
            set({ isUserDataLoading: false });
            return;
          }
          throw new Error(`Failed to fetch user data: ${response.status}`);
        }

        const userData = await response.json();
        set({ 
          userEmail: userData.data.email,
          userName: userData.data.first_name,
          userId: userData.data.id,
          isUserDataLoaded: true,
          isUserDataLoading: false
        });
        return;
      } catch (error) {
        lastError = error as Error;
        retryCount++;
        
        if (retryCount <= maxRetries) {
          const delay = Math.min(1000, Math.pow(2, retryCount) * 100);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed
    console.error("Error fetching user data after retries:", lastError);
    set({ isUserDataLoading: false });
  },

  handleLogout: async () => {
    try {
      const response = await fetch("/api/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to logout.");
      }

      set({ 
        userName: null, 
        userId: null, 
        userEmail: null, 
        userImage: '',
        isUserDataLoaded: false,
        isUserDataLoading: false
      });
    } catch (error: any) {
      console.error("Error logging out:", error.message);
      throw error;
    }
  }
}));